/**
 * LocalProvider — the zero-dependency, on-device intelligence engine.
 *
 * Runs instantly with no network and no API key. Serves as both the free-tier
 * engine and the offline fallback when an LLM provider is unavailable.
 * It cannot read images — paper-scan OCR requires a vision-capable provider
 * (add a Gemini key in Settings); the store surfaces that gracefully.
 */

import type { ActionStep, AiProvider, ClassifiedTask, ScanAttachment, ZoneId } from '../types';
import { classifyDump } from './classifier';
import { generateSteps } from './steps';
import { fallbackRevision } from './planUpdate';

/** Small artificial delay so the "Magic Sort" animation reads as real work. */
const cosmeticDelay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class LocalProvider implements AiProvider {
  readonly name = 'ZoneIn Local Engine';
  readonly supportsVision = false;

  async classify(rawInput: string, _scans?: ScanAttachment[]): Promise<ClassifiedTask[]> {
    await cosmeticDelay(400);
    return classifyDump(rawInput);
  }

  async expand(
    taskText: string,
    zone: ZoneId,
  ): Promise<Omit<ActionStep, 'id' | 'done'>[]> {
    await cosmeticDelay(300);
    return generateSteps(taskText, zone);
  }

  async revise(
    _taskText: string,
    _zone: ZoneId,
    _completed: ActionStep[],
    open: ActionStep[],
    note: string,
  ): Promise<Omit<ActionStep, 'id' | 'done'>[]> {
    await cosmeticDelay(300);
    // No LLM: keep the open plan and graft steps parsed from the note.
    return fallbackRevision(open, note);
  }
}
